import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  boolean,
  index,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Domain Purchase Status
// ---------------------------------------------------------------------------

export const DOMAIN_PURCHASE_STATUSES = [
  "pending_payment",
  "registering",
  "active",
  "expired",
  "transfer_out",
  "transferred",
  "failed",
  "cancelled",
] as const;

export type DomainPurchaseStatus = (typeof DOMAIN_PURCHASE_STATUSES)[number];

export const DOMAIN_TRANSFER_STATUSES = [
  "requested",
  "auth_code_sent",
  "in_progress",
  "completed",
  "failed",
  "cancelled",
] as const;

export type DomainTransferStatus = (typeof DOMAIN_TRANSFER_STATUSES)[number];

// ---------------------------------------------------------------------------
// Domain Purchases — Full lifecycle from search to renewal
// ---------------------------------------------------------------------------

export const domainPurchases = pgTable(
  "domain_purchases",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    customDomainId: uuid("custom_domain_id"),
    domain: text("domain").notNull(),
    tld: text("tld").notNull(),
    registrar: text("registrar").notNull(),
    registrarOrderId: text("registrar_order_id"),
    status: text("status").notNull().default("pending_payment"),
    purchasePriceCents: integer("purchase_price_cents").notNull(),
    renewalPriceCents: integer("renewal_price_cents"),
    currency: text("currency").notNull().default("usd"),
    registrationYears: integer("registration_years").notNull().default(1),
    autoRenew: boolean("auto_renew").notNull().default(true),
    whoisPrivacy: boolean("whois_privacy").notNull().default(true),
    registeredAt: timestamp("registered_at", tz),
    expiresAt: timestamp("expires_at", tz),
    lastRenewedAt: timestamp("last_renewed_at", tz),
    stripePaymentIntentId: text("stripe_payment_intent_id"),
    purchasedBy: uuid("purchased_by"),
    notes: text("notes"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_domain_purchases_tenant").on(table.tenantId),
    index("idx_domain_purchases_domain").on(table.domain),
    index("idx_domain_purchases_status").on(table.status),
    index("idx_domain_purchases_expiry").on(table.expiresAt),
    check(
      "chk_domain_purchase_status",
      sql`${table.status} IN ('pending_payment', 'registering', 'active', 'expired', 'transfer_out', 'transferred', 'failed', 'cancelled')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Domain DNS Records — Managed DNS for platform-purchased domains
// ---------------------------------------------------------------------------

export const domainDnsRecords = pgTable(
  "domain_dns_records",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    customDomainId: uuid("custom_domain_id").notNull(),
    recordType: text("record_type").notNull(),
    name: text("name").notNull(),
    value: text("value").notNull(),
    ttl: integer("ttl").notNull().default(3600),
    priority: integer("priority"),
    registrarRecordId: text("registrar_record_id"),
    isAutoManaged: boolean("is_auto_managed").notNull().default(true),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_dns_records_domain").on(table.customDomainId),
    index("idx_dns_records_tenant").on(table.tenantId),
    check(
      "chk_dns_record_type",
      sql`${table.recordType} IN ('A', 'AAAA', 'CNAME', 'MX', 'TXT', 'SRV', 'NS')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Domain Transfers — Transfer OUT to customer
// ---------------------------------------------------------------------------

export const domainTransfers = pgTable(
  "domain_transfers",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    domainPurchaseId: uuid("domain_purchase_id").notNull(),
    domain: text("domain").notNull(),
    status: text("status").notNull().default("requested"),
    requestedBy: uuid("requested_by").notNull(),
    requestedAt: timestamp("requested_at", tz).notNull().defaultNow(),
    authCodeSentAt: timestamp("auth_code_sent_at", tz),
    authCodeExpiresAt: timestamp("auth_code_expires_at", tz),
    transferInitiatedAt: timestamp("transfer_initiated_at", tz),
    completedAt: timestamp("completed_at", tz),
    failedAt: timestamp("failed_at", tz),
    failureReason: text("failure_reason"),
    targetRegistrar: text("target_registrar"),
    dnsRecordsExported: boolean("dns_records_exported").notNull().default(false),
    customDomainDetached: boolean("custom_domain_detached").notNull().default(false),
    approvedBy: uuid("approved_by"),
    approvedAt: timestamp("approved_at", tz),
    notes: text("notes"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_domain_transfers_tenant").on(table.tenantId),
    index("idx_domain_transfers_purchase").on(table.domainPurchaseId),
    index("idx_domain_transfers_status").on(table.status),
    check(
      "chk_domain_transfer_status",
      sql`${table.status} IN ('requested', 'auth_code_sent', 'in_progress', 'completed', 'failed', 'cancelled')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Domain Policies — Governance rules per domain
// ---------------------------------------------------------------------------

export const domainPolicies = pgTable(
  "domain_policies",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    customDomainId: uuid("custom_domain_id").notNull(),
    transferLockDays: integer("transfer_lock_days").notNull().default(60),
    requireAdminApprovalForTransfer: boolean("require_admin_approval_for_transfer").notNull().default(false),
    autoRenewEnabled: boolean("auto_renew_enabled").notNull().default(true),
    renewalReminderDays: integer("renewal_reminder_days").notNull().default(30),
    maxRenewalYears: integer("max_renewal_years").notNull().default(10),
    whoisPrivacyEnabled: boolean("whois_privacy_enabled").notNull().default(true),
    dnssecEnabled: boolean("dnssec_enabled").notNull().default(false),
    parkingPageEnabled: boolean("parking_page_enabled").notNull().default(true),
    parkingRedirectUrl: text("parking_redirect_url"),
    allowSubdomains: boolean("allow_subdomains").notNull().default(true),
    maxSubdomains: integer("max_subdomains").notNull().default(10),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_domain_policies_tenant").on(table.tenantId),
    index("idx_domain_policies_domain").on(table.customDomainId),
  ],
);
